import { showLoadingToast, loadingToSuccess, loadingToError } from '@components/Notify/Toast';
import { queryClient } from '@src/providers/QueryClient';
import { getBaseUrl, getUserToken } from '@src/API/helpers';

const fetchOperationStatus = async (operationId) => {
  try {
    const response = await fetch(`${getBaseUrl()}/api/operations/${operationId}/status`, {
      headers: { Accept: 'application/json', Authorization: `Bearer ${getUserToken()}` }
    });
    if (!response?.ok) return null;
    const body = await response?.json();
    return body?.data?.status ?? null;
  } catch {
    return null;
  }
};

export const refreshAffectedList = (context) => {
  const key = context?.invalidateKeys || context?.queryKey;
  if (key) queryClient.invalidateQueries({ queryKey: Array.isArray(key) ? key : [key], refetchType: 'all' });
  else queryClient.invalidateQueries();
};

/**
 * Factory for the "queued async operation" pattern shared by bulk-delete, excel
 * import and any future long-running job the backend runs off the request thread.
 *
 * The backend acknowledges over HTTP with `{ status: 'queued', operation_id }`,
 * then broadcasts a realtime completion later. This tracker bridges the two:
 *
 *  - `handleQueuedResponse` registers the HTTP ack, shows a loading toast, and
 *    remembers the caller's `context` (queryKey / invalidateKeys / onComplete …).
 *  - `completeOperation` (called by the realtime handler) returns that context so
 *    the completion can resolve the SAME toast and refresh the exact list.
 *
 * It also handles the race where a very small job finishes and broadcasts before
 * the HTTP response reaches the browser: the completion is remembered briefly so
 * the late ack does not replace the already-shown final toast with a stale one.
 *
 * @param {object}   opts
 * @param {string}   opts.prefix          Toast-id prefix, e.g. 'bulk-delete' → `bulk-delete-<id>`.
 * @param {string}   opts.loadingMessage  Message for the loading toast.
 * @param {number}  [opts.pendingTtl]     ms to keep a pending op before dropping it.
 * @param {number}  [opts.completedTtl]   ms to remember an early completion.
 * @param {number}  [opts.toastDuration]  ms the loading toast stays up.
 * @param {number}  [opts.fallbackInterval] ms of realtime silence before each status poll.
 * @param {Function}[opts.getPayload]     (response) => payload. Defaults to response.data.data.
 * @param {Function}[opts.getOperationId] (payload)  => id. Defaults to payload.operation_id.
 */
export const createAsyncOperationTracker = ({
  prefix,
  loadingMessage,
  pendingTtl = 120000,
  completedTtl = 120000,
  toastDuration = 70000,
  fallbackInterval = 30000,
  getPayload = (response) => response?.data?.data ?? null,
  getOperationId = (payload) => payload?.operation_id
}) => {
  const pendingOperations = new Map();
  const recentlyCompletedOperations = new Map();

  const resolveFromFallback = (operationId, entry, status) => {
    const toastId = getToastId(operationId);
    if (status === 'failed') loadingToError(toastId, 'Operation failed.');
    else loadingToSuccess(toastId, 'Operation completed.');

    refreshAffectedList(entry);
    entry?.onComplete?.({ operation_id: operationId, status }, {});
  };

  const pollOperationStatus = async (operationId) => {
    if (!pendingOperations.has(operationId)) return;

    const status = await fetchOperationStatus(operationId);

    const entry = pendingOperations.get(operationId);
    if (!entry) return;

    if (status === 'done' || status === 'failed') {
      completeOperation(operationId);
      resolveFromFallback(operationId, entry, status);
    } else {
      entry.fallbackTimer = setTimeout(() => pollOperationStatus(operationId), fallbackInterval);
    }
  };

  const isQueuedResponse = (response) => {
    const payload = getPayload(response);
    return payload?.status === 'queued' && Boolean(getOperationId(payload));
  };

  const getToastId = (operationId) => `${prefix}-${operationId}`;

  /**
   * Registers the HTTP acknowledgement so the realtime completion can refresh the
   * exact list and update the same loading toast. Returns true when the response
   * was a queued ack (caller should skip its synchronous refresh), false otherwise.
   */
  const handleQueuedResponse = (response, context = {}) => {
    if (!isQueuedResponse(response)) return false;

    const operationId = getOperationId(getPayload(response));

    // Completion beat the ack to the browser: its final toast already showed, so
    // do not replace it with a stale loading toast.
    if (recentlyCompletedOperations.has(operationId)) {
      const completion = recentlyCompletedOperations.get(operationId);
      recentlyCompletedOperations.delete(operationId);
      refreshAffectedList(context);
      context?.onComplete?.(completion, {});
      return true;
    }

    const cleanupTimer = setTimeout(() => {
      clearTimeout(pendingOperations.get(operationId)?.fallbackTimer);
      pendingOperations.delete(operationId);
    }, pendingTtl);

    pendingOperations.set(operationId, {
      ...context,
      cleanupTimer,
      fallbackTimer: setTimeout(() => pollOperationStatus(operationId), fallbackInterval)
    });
    showLoadingToast(context.loadingMessage ?? loadingMessage, {
      id: getToastId(operationId),
      duration: toastDuration
    });

    return true;
  };

  /** Returns the dispatch-time context, or records an early completion race. */
  const completeOperation = (operationId, result = { operation_id: operationId }) => {
    const pending = operationId ? pendingOperations.get(operationId) : null;

    if (pending) {
      clearTimeout(pending.cleanupTimer);
      clearTimeout(pending.fallbackTimer);
      pendingOperations.delete(operationId);
      return pending;
    }

    if (operationId) {
      recentlyCompletedOperations.set(operationId, result);
      setTimeout(() => recentlyCompletedOperations.delete(operationId), completedTtl);
    }

    return null;
  };

  return { isQueuedResponse, handleQueuedResponse, completeOperation, getToastId, getPayload };
};
